import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  PencilSquareIcon,
  TrashIcon,
  PaperClipIcon,
  CheckCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useKeycloak } from '@react-keycloak/web';
import { useBillingStore } from '../store/billingStore';
import { useBilling } from '../hooks/useBilling';
import { EXPENSE_CATEGORY_LABELS, BILLING_STATUS_LABELS, BILLING_STATUS_TRANSITIONS } from '../types/billing.types';
import type { Expense, BillingStatus } from '../types/billing.types';
import BillingStatusBadge from './BillingStatusBadge';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../../../components/ui/dropdown-menu';
import ConfirmModal from './ConfirmModal';

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const API_BASE = import.meta.env.VITE_API_URL || '';

const ExpensesView: React.FC = () => {
  const { keycloak } = useKeycloak();
  const store = useBillingStore();
  const {
    deleteExpense,
    bulkApproveExpenses,
    transitionExpenseBillingStatus,
    bulkTransitionExpenseBillingStatus,
  } = useBilling();
  const [receiptModal, setReceiptModal] = useState<{ expenseId: string; fileId: string } | null>(null);
  const [receiptImageUrl, setReceiptImageUrl] = useState<string | null>(null);
  const [receiptLoading, setReceiptLoading] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const handleEdit = (expense: Expense) => {
    store.openDrawer('expense', 'edit', expense.expense_id);
  };

  const handleViewReceipt = (e: React.MouseEvent, expense: Expense) => {
    e.stopPropagation();
    if (expense.has_receipt && expense.receipt_attachments?.length) {
      setReceiptModal({ expenseId: expense.expense_id, fileId: expense.receipt_attachments[0] });
    }
  };

  // Fetch receipt image with auth headers and create blob URL
  useEffect(() => {
    if (!receiptModal) {
      if (receiptImageUrl) {
        URL.revokeObjectURL(receiptImageUrl);
        setReceiptImageUrl(null);
      }
      return;
    }

    let cancelled = false;
    setReceiptLoading(true);

    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/v1/billing/expenses/${receiptModal.expenseId}/receipt/${encodeURIComponent(receiptModal.fileId)}`,
          { headers: { Authorization: `Bearer ${keycloak?.token || ''}` } }
        );
        if (!res.ok) throw new Error('Failed to load receipt');
        const blob = await res.blob();
        if (!cancelled) {
          setReceiptImageUrl(URL.createObjectURL(blob));
        }
      } catch {
        if (!cancelled) setReceiptImageUrl(null);
      } finally {
        if (!cancelled) setReceiptLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [receiptModal, keycloak?.token]);

  const handleDelete = (id: string) => {
    setDeleteConfirm(id);
  };

  const confirmDelete = async () => {
    if (deleteConfirm) {
      await deleteExpense(deleteConfirm);
      setDeleteConfirm(null);
    }
  };

  const handleBulkApprove = async () => {
    if (store.selectedExpenseIds.length === 0) return;
    await bulkApproveExpenses(store.selectedExpenseIds);
  };

  const handleBulkStatusTransition = async (newStatus: BillingStatus) => {
    if (store.selectedExpenseIds.length === 0) return;
    await bulkTransitionExpenseBillingStatus(store.selectedExpenseIds, newStatus);
  };

  const commonBulkTransitions = useMemo(() => {
    if (store.selectedExpenseIds.length === 0) return [] as BillingStatus[];
    const selectedItems = store.expenses.filter((e) =>
      store.selectedExpenseIds.includes(e.expense_id)
    );
    if (selectedItems.length === 0) return [] as BillingStatus[];
    const sets = selectedItems.map((e) => new Set(BILLING_STATUS_TRANSITIONS[e.billing_status]));
    const intersection = [...sets[0]].filter((s) => sets.every((set) => set.has(s)));
    return intersection;
  }, [store.selectedExpenseIds, store.expenses]);

  if (store.expenses.length === 0 && !store.loading) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-500 dark:text-gray-400 text-lg">No expenses found.</p>
        <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">
          Create a new expense to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Bulk actions */}
      {store.selectedExpenseIds.length > 0 && (
        <div className="flex items-center gap-3 bg-lex-accent-soft border border-lex-accent rounded-lg px-4 py-2">
          <span className="text-sm text-lex-accent">
            {store.selectedExpenseIds.length} selected
          </span>
          <button
            onClick={handleBulkApprove}
            className="flex items-center gap-1 text-sm px-3 py-1 bg-lex-accent text-lex-accent-contrast rounded hover:bg-lex-accent-hover transition-colors"
          >
            <CheckCircleIcon className="h-4 w-4" />
            Approve Selected
          </button>
          {commonBulkTransitions.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger>
                <span className="text-sm">Change Status</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {commonBulkTransitions.filter((s) => s !== 'written_off').map((target) => (
                  <DropdownMenuItem key={target} onClick={() => handleBulkStatusTransition(target)}>
                    {BILLING_STATUS_LABELS[target]}
                  </DropdownMenuItem>
                ))}
                {commonBulkTransitions.includes('written_off') && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => handleBulkStatusTransition('written_off')}
                      className="text-red-600 dark:text-red-400"
                    >
                      {BILLING_STATUS_LABELS.written_off}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <button
            onClick={store.clearSelections}
            className="text-sm text-lex-accent hover:underline"
          >
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <th className="px-3 py-3 text-left">
                <input
                  type="checkbox"
                  checked={
                    store.expenses.length > 0 &&
                    store.selectedExpenseIds.length === store.expenses.length
                  }
                  onChange={store.selectAllExpenses}
                  className="rounded border-gray-300 dark:border-gray-600"
                />
              </th>
              <th className="px-3 py-3 text-left text-gray-600 dark:text-gray-300 font-medium">Date</th>
              <th className="px-3 py-3 text-left text-gray-600 dark:text-gray-300 font-medium">Matter</th>
              <th className="px-3 py-3 text-left text-gray-600 dark:text-gray-300 font-medium">Description</th>
              <th className="px-3 py-3 text-left text-gray-600 dark:text-gray-300 font-medium">Category</th>
              <th className="px-3 py-3 text-left text-gray-600 dark:text-gray-300 font-medium">Vendor</th>
              <th className="px-3 py-3 text-right text-gray-600 dark:text-gray-300 font-medium">Amount</th>
              <th className="px-3 py-3 text-center text-gray-600 dark:text-gray-300 font-medium">Receipt</th>
              <th className="px-3 py-3 text-center text-gray-600 dark:text-gray-300 font-medium">Status</th>
              <th className="px-3 py-3 text-right text-gray-600 dark:text-gray-300 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {store.expenses.map((expense, idx) => (
              <tr
                key={expense.expense_id}
                className="lex-list-row hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
                style={{ '--row-index': idx } as React.CSSProperties}
                onClick={() => handleEdit(expense)}
              >
                <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={store.selectedExpenseIds.includes(expense.expense_id)}
                    onChange={() => store.toggleExpenseSelection(expense.expense_id)}
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                </td>
                <td className="px-3 py-3 text-gray-900 dark:text-white whitespace-nowrap">
                  {formatDate(expense.expense_date)}
                </td>
                <td className="px-3 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap max-w-[140px] truncate" title={expense.matter?.matter_number ? `${expense.matter.matter_number}${expense.matter.description ? ` — ${expense.matter.description}` : ''}` : ''}>
                  {expense.matter?.matter_number || '-'}
                </td>
                <td className="px-3 py-3 text-gray-900 dark:text-white max-w-xs truncate">
                  <div className="flex items-center gap-2">
                    <span className="truncate">{expense.description}</span>
                    {expense.ai_categorized && (
                      <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-lex-accent-soft text-lex-accent rounded">
                        AI
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  {EXPENSE_CATEGORY_LABELS[expense.expense_category] || expense.expense_category}
                </td>
                <td className="px-3 py-3 text-gray-700 dark:text-gray-300 max-w-[160px] truncate" title={expense.vendor || ''}>
                  {expense.vendor || '-'}
                </td>
                <td className="px-3 py-3 text-right text-gray-900 dark:text-white font-medium tabular-nums whitespace-nowrap">
                  {formatCurrency(Number(expense.total_amount))}
                </td>
                <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={(e) => handleViewReceipt(e, expense)}
                    disabled={!expense.has_receipt}
                    className={`p-0.5 rounded ${expense.has_receipt ? 'hover:bg-green-50 dark:hover:bg-green-900/20 cursor-pointer' : 'cursor-default'}`}
                    title={expense.has_receipt ? 'View receipt' : expense.receipt_required ? 'Receipt required' : 'No receipt'}
                  >
                    <PaperClipIcon
                      className={`h-4 w-4 mx-auto ${
                        expense.has_receipt
                          ? 'text-green-500'
                          : expense.receipt_required
                            ? 'text-red-400'
                            : 'text-gray-300 dark:text-gray-600'
                      }`}
                    />
                  </button>
                </td>
                <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                  <BillingStatusBadge
                    status={expense.billing_status || 'unbilled'}
                    isNonBillable={expense.is_non_billable}
                    entityType="expense"
                    entityId={expense.expense_id}
                    onTransition={transitionExpenseBillingStatus}
                  />
                </td>
                <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                  {!expense.is_billed && (
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleEdit(expense)}
                        className="p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                        title="Edit"
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(expense.expense_id)}
                        className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination info */}
      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 px-1">
        <span>
          Showing {store.expenses.length} of {store.expensePagination.total} expenses
        </span>
        {store.expensePagination.has_more && (
          <button
            onClick={() => {
              const newOffset = (store.expenseFilters.offset || 0) + (store.expenseFilters.limit || 50);
              store.setExpenseFilters({ offset: newOffset });
            }}
            className="text-lex-accent hover:underline"
          >
            Load more
          </button>
        )}
      </div>

      {/* Receipt Image Modal */}
      {receiptModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
          onClick={() => setReceiptModal(null)}
        >
          <div
            className="relative max-w-2xl max-h-[90vh] bg-white dark:bg-gray-900 rounded-lg shadow-xl p-2"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setReceiptModal(null)}
              className="absolute top-2 right-2 z-10 p-1 bg-gray-100 dark:bg-gray-800 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              <XMarkIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            </button>
            {receiptLoading && (
              <div className="flex items-center justify-center py-16 px-24">
                <div className="animate-spin h-8 w-8 border-2 border-lex-accent border-t-transparent rounded-full" />
              </div>
            )}
            {!receiptLoading && receiptImageUrl && (
              <img
                src={receiptImageUrl}
                alt="Receipt"
                className="max-w-full max-h-[85vh] object-contain rounded"
              />
            )}
            {!receiptLoading && !receiptImageUrl && (
              <p className="text-center text-gray-500 py-8 px-12">Failed to load receipt image.</p>
            )}
          </div>
        </div>
      )}

      <ConfirmModal
        open={deleteConfirm !== null}
        title="Delete Expense"
        message="This expense will be permanently deleted. This action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  );
};

export default ExpensesView;
